import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import {
    Clock,
    DollarSign,
    TrendingUp,
    RefreshCw,
    Sparkles,
} from "lucide-react";

const roiMetrics = [
    {
        icon: Clock,
        value: "70%",
        label: "Less Time on Documentation",
        detail: "Auto-generated SOAP notes replace hours of manual writing with seconds of AI automation",
        color: "text-brand-600",
        bgColor: "bg-brand-50",
    },
    {
        icon: DollarSign,
        value: "35%",
        label: "First-Pass Approval Rate",
        detail: "AI-extracted ICD codes and pre-submission denial risk checks protect your revenue",
        color: "text-accent-600",
        bgColor: "bg-accent-50",
    },
    {
        icon: TrendingUp,
        value: "3x",
        label: "More Patient Face Time",
        detail: "When admin disappears, you can see more patients or spend more quality time with each one",
        color: "text-violet-600",
        bgColor: "bg-violet-50",
    },
    {
        icon: RefreshCw,
        value: "< 1 Week",
        label: "Migration Time",
        detail: "Switch from any platform with minimal disruption. Our team handles the heavy lifting.",
        color: "text-amber-600",
        bgColor: "bg-amber-50",
    },
];

export default function ROISection() {
    const { ref, inView } = useScrollAnimation();

    return (
        <section
            className="section-padding bg-gradient-to-b from-gray-50 to-white"
            ref={ref}
        >
            <div className="container-max">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center max-w-3xl mx-auto mb-16"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 rounded-full text-sm font-medium text-accent-700 mb-4">
                        <Sparkles className="w-4 h-4" />
                        The ROI of AI-Powered Care
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
                        Your Time Is Worth More Than{" "}
                        <span className="gradient-text">Paperwork</span>
                    </h2>
                    <p className="text-lg text-gray-600">
                        Switching from traditional platforms to Doctorite AI
                        delivers measurable ROI from week one. Here's what our
                        providers experience.
                    </p>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                    {roiMetrics.map((metric, index) => (
                        <motion.div
                            key={metric.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="bg-white rounded-2xl p-6 shadow-lg shadow-brand-900/5 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                        >
                            <div
                                className={`w-12 h-12 ${metric.bgColor} rounded-xl flex items-center justify-center mb-4`}
                            >
                                <metric.icon
                                    className={`w-6 h-6 ${metric.color}`}
                                />
                            </div>
                            <div
                                className={`text-4xl font-extrabold ${metric.color} mb-1`}
                            >
                                {metric.value}
                            </div>
                            <div className="text-sm font-bold text-gray-900 mb-2">
                                {metric.label}
                            </div>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                {metric.detail}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Migration CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')] opacity-60" />
                    <div className="relative">
                        <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
                            Switching From Another Platform?
                        </h3>
                        <p className="text-brand-200 max-w-2xl mx-auto mb-8 text-base">
                            Our dedicated migration team ensures a seamless
                            transition with zero data loss and minimal
                            disruption to your practice.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            {/* <a href="#contact" className="btn-accent !py-4 !px-8 !text-base group">
                Plan My Migration
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a> */}
                            <Link
                                to="/register"
                                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/25 rounded-xl hover:bg-white/10 transition-all duration-200"
                            >
                                Try Free for 7 Days
                            </Link>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
